import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Box, Button, DialogContent } from '@mui/material';
import './land-visualization-modal.scss';
import { RegridResponse } from '../../models/regrid.model.tsx';
import { PropertyDetailsResponseData } from '../../models/details.models.tsx';
import { ChangeEvent, useCallback, useState } from 'react';
import { HomeModelsResponseData } from '../../models/homes.models.tsx';
import { HomePlacement } from './types/position.ts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SetbacksMoreInfo } from './components/setbacks-more-info/setbacks-more-info.tsx';
import { HomeSelectList } from './components/home-select-list/home-select-list.tsx';
import { VisualizationSelect } from './components/visualization-select/visualization-select.tsx';
import { ThreeDimensionHomeViewer } from './3d-home-viewer.tsx';
import { TwoDimensionHomeViewer } from './2d-home-viewer.tsx';
import { LandVisualizationDropdownTipsModal } from './components/dropdown-tips-modal/land-visualization-dropdown-tips-modal.tsx';
import { BuilderViewLayerSelectionModal } from './components/builder-view-layer-selection-modal/builder-view-layer-selection-modal.tsx';

interface LandVisualizationModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  regridData: RegridResponse | undefined;
  propertyData: PropertyDetailsResponseData | undefined;
  mlsImage?: string;
}

export const LandVisualizationModal = (props: LandVisualizationModalProps) => {
  const { isOpen, setIsOpen, regridData, propertyData } = props;

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const [selectedOverlay, setSelectedOverlay] = useState('');
  const [showingBuilderViewLayersModal, setShowingBuilderViewLayersModal] =
    useState(false);
  const [selectedOverlayTip, setSelectedOverlayTip] = useState('');

  const [selectedHomeModel, setSelectedHomeModel] = useState('');
  const [selectedHomeObject, setSelectedHomeObject] = useState(
    null as null | HomeModelsResponseData
  );

  const [selectedBuilderViewLayers, setSelectedBuilderViewLayers] = useState([
    'Setbacks',
    'Topography',
    'Flood Zone',
    'Wetlands',
    'Home Placement',
  ]);

  const [mapViewType, setMapViewType] = useState('map');

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSelectedHomeModel(event.target.value);
  }, []);

  // TODO: This is derived state, not needed to be stored as an array
  const toggleBuilderViewLayer = useCallback(
    (layerToToggle: string) => {
      if (selectedBuilderViewLayers.includes(layerToToggle)) {
        // Layer is selected, remove it
        setSelectedBuilderViewLayers(
          selectedBuilderViewLayers.filter((item) => item !== layerToToggle)
        );
      } else {
        // Layer is not selected, add it
        setSelectedBuilderViewLayers([
          ...selectedBuilderViewLayers,
          layerToToggle,
        ]);
      }
    },
    [selectedBuilderViewLayers]
  );

  // This state is shared between 2d/3d viewer.
  const [show3dView, setShow3dView] = useState(false);
  const [homePlacement, setHomePlacement] = useState({
    latitude: propertyData?.latitude || 0,
    longitude: propertyData?.longitude || 0,
    rotation: 0,
  } as HomePlacement);

  return (
    <>
      <Dialog
        data-testid="land-visualization-modal"
        className="land-visualization-modal"
        fullScreen={fullScreen}
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="land-visualization-modal-title"
        fullWidth
        maxWidth="lg"
        sx={{
          minWidth: { xs: 'initial', sm: '100%' },
          maxWidth: { xs: 'initial', sm: '100%' },
          height: '100%',
        }}
      >
        <Box
          className="container"
          sx={{ minWidth: { xs: 'initial', sm: '100%' }, height: '100%' }}
        >
          <DialogContent
            className="content"
            sx={{ height: { xs: '100dvh', sm: '80vh' } }}
          >
            <Box className="back-details-container">
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  marginTop: '12px',
                  marginRight: '12px',
                  minWidth: '48px',
                  height: '36px',
                  background: 'white',
                }}
              >
                <ArrowBackIcon />
                BACK
              </Button>
              {(selectedOverlay === 'Setbacks' ||
                selectedOverlay === 'Builder View') && (
                <SetbacksMoreInfo
                  selectedOverlay={selectedOverlay}
                  setShowingBuilderViewLayersModal={
                    setShowingBuilderViewLayersModal
                  }
                  regridData={regridData}
                />
              )}
            </Box>
            {(selectedOverlay === 'Home Placement' ||
              selectedOverlay === 'Builder View') && (
              <HomeSelectList
                selectedHomeModel={selectedHomeModel}
                handleChange={handleChange}
                setSelectedHomeObject={setSelectedHomeObject}
              />
            )}
            {!show3dView && (
              <Box className="visualization-select-container">
                <VisualizationSelect
                  setSelectedOverlay={setSelectedOverlay}
                  setMapViewType={setMapViewType}
                  selectedOverlay={selectedOverlay}
                />
              </Box>
            )}
            <Box className="show-visualization-map-button">
              {Boolean(selectedHomeObject?.id) && (
                <Button
                  sx={{ textTransform: 'capitalize', borderRadius: '54px' }}
                  onClick={() => setShow3dView(!show3dView)}
                  variant="contained"
                >
                  {show3dView ? 'Show Map' : 'Show 3D View'}
                </Button>
              )}
            </Box>

            {show3dView && regridData && selectedHomeObject ? (
              <ThreeDimensionHomeViewer
                regridData={regridData}
                selectedHomeModelSku={selectedHomeObject.sku}
                homePlacement={homePlacement}
                setHomePlacement={setHomePlacement}
              />
            ) : (
              <>
                <TwoDimensionHomeViewer
                  addressDetails={propertyData}
                  regridData={regridData}
                  selectedOverlay={selectedOverlay}
                  selectedHomeObject={selectedHomeObject}
                  selectedBuilderViewLayers={selectedBuilderViewLayers}
                  homePlacement={homePlacement}
                  setHomePlacement={setHomePlacement}
                  mapViewType={mapViewType}
                  setMapViewType={setMapViewType}
                />

                <LandVisualizationDropdownTipsModal
                  selectedOverlayTip={selectedOverlayTip}
                  setSelectedOverlayTip={setSelectedOverlayTip}
                />

                <BuilderViewLayerSelectionModal
                  showingModal={showingBuilderViewLayersModal}
                  setShowingModal={setShowingBuilderViewLayersModal}
                  toggleBuilderViewLayer={toggleBuilderViewLayer}
                  selectedLayers={selectedBuilderViewLayers}
                />
              </>
            )}
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};


